/**
 * @file: useCheckExistingOrder.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { kNullVIN } from '@euler/functions/constants';
import { ReceptionOrderStatus } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { withConfirmation } from '@euler/utils';
import { array2map } from '@euler/utils/array';
import { formatDateTime } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { isValidLicensePlateNo } from '@euler/utils/validators';
import { OrderPropertyName, kDefaultAutoFillProperties } from './constants';

export function useCheckExistingOrder(): (
  mode: 'vin' | 'plate',
  value: string,
) => Promise<void> {
  const {
    basicInfo,
    skipDirtyCheck,
    vehicleImageUrl$,
    formController: { form },
    onNavigateToExistingTask,
    finish,
    storeConfiguration,
    updateDetail,
  } = useOrderFlowContext();

  const { orderService, vehicleInfoService } = useServiceFactory();

  return usePersistFn(async (mode: 'vin' | 'plate', value: string) => {
    if (mode === 'vin' && value === kNullVIN) {
      return;
    }

    if (onNavigateToExistingTask == null || basicInfo != null || !value) {
      return;
    }

    const args: {
      vin?: string;
      licensePlateNo?: string;
    } = {};

    const fieldState = mode === 'plate' ? form.licensePlateNo : form.vin;

    if (mode === 'plate') {
      args.licensePlateNo = value;
      if (form.vin.value) {
        args.vin = form.vin.value;
      }
    } else {
      args.vin = value;
      if (form.licensePlateNo.value) {
        args.licensePlateNo = form.licensePlateNo.value;
      }
    }

    try {
      fieldState.setLoading(true);

      const [order, existingVehicleInfo] = await Promise.all([
        orderService.getLatestOrderInfo(args),
        vehicleInfoService.getExistingVehicleInfo(args),
      ]);

      fieldState.setLoading(false);

      if (order == null) return;

      const performAutoFill = () => {
        //#region auto fill fields from previous order information

        const shouldAutoFill = (property: OrderPropertyName) => {
          const autoFill =
            storeConfiguration.app?.orderFlow?.fields?.[property]?.autoFill;
          return (
            autoFill ||
            (kDefaultAutoFillProperties.has(property) && autoFill !== false)
          );
        };

        if (order?.customerName && shouldAutoFill('customerName')) {
          form.customerName.update(order?.customerName);
        }

        if (order?.customerMobile && shouldAutoFill('customerMobile')) {
          form.customerMobile.update(order?.customerMobile);
        }

        if (order?.customerType && shouldAutoFill('customerType')) {
          form.customerType.update(order?.customerType);
        }

        if (
          order?.insuranceExpiryDate &&
          shouldAutoFill('insuranceExpiryDate')
        ) {
          form.insuranceExpiryDate.update(new Date(order?.insuranceExpiryDate));
        }

        if (order?.detail?.extendedProps != null) {
          const extendedProps = order?.detail?.extendedProps;
          const map = array2map(
            storeConfiguration.extendedOrderProperties ?? [],
            x => x.id,
          );
          updateDetail(draft => {
            for (const extendedProp of extendedProps) {
              const id = extendedProp.id;
              const def = map.get(id);

              if (!def?.autoFill) continue;

              const target = draft.extendedProps?.find(x => x.id === id);
              if (!target) {
                if (!draft.extendedProps) {
                  draft.extendedProps = [];
                }
                draft.extendedProps.push({ id, value: extendedProp.value });
              } else {
                target.value = extendedProp.value;
              }
            }
          });
        }

        //#endregion

        if (existingVehicleInfo != null) {
          // automatically fill up rest information.
          if (
            form.licensePlateNo.value !== existingVehicleInfo.licensePlateNo
          ) {
            form.isSpecialLicensePlateNo.update(
              !isValidLicensePlateNo(existingVehicleInfo.licensePlateNo),
            );
            form.licensePlateNo.update(existingVehicleInfo.licensePlateNo);
          }
          // The vehicle was received before without an ISO VIN, so restore
          // that mode rather than making the shop declare it again — and
          // leave the VIN field alone, since the placeholder it stores is not
          // a number anyone typed.
          if (existingVehicleInfo.chassisNo) {
            form.isNonStandardVin.update(true);
            form.chassisNo.update(existingVehicleInfo.chassisNo);
          } else if (form.vin.value !== existingVehicleInfo.vin) {
            form.vin.update(existingVehicleInfo.vin);
          }

          // create the service information.
          form.vehicleInfo.update({
            vehicleName: existingVehicleInfo.vehicleName,
            vehicleImageUrl: existingVehicleInfo.vehicleImageUrl ?? undefined,
            vehicleBrandName: existingVehicleInfo.vehicleBrandName,
            vehicleBrandLogo: existingVehicleInfo.vehicleBrandLogo ?? undefined,
            vehicleManufacturer:
              existingVehicleInfo.vehicleManufacturer ?? undefined,
            vehicleFuelType: existingVehicleInfo.vehicleFuelType ?? undefined,
            vehicleModel: existingVehicleInfo.vehicleModel ?? undefined,
            vehicleModelYear: existingVehicleInfo.vehicleModelYear ?? undefined,
            vehicleDisplacement:
              existingVehicleInfo.vehicleDisplacement ?? undefined,
          });

          vehicleImageUrl$.next(
            existingVehicleInfo.vehicleImageUrl ?? undefined,
          );
        }
      };

      if (
        order.status !== ReceptionOrderStatus.Finished &&
        order.taskNo != null
      ) {
        const taskNo = order.taskNo;
        const message = [
          '已存在',
          mode === 'plate' ? '车牌号' : '车架号',
          '为',
          value,
          `的在厂服务单(${order.vehicleName}, 服务顾问: ${
            order.serviceAgentName
          }, 进店时间: ${formatDateTime(order.createdAt)})`,
        ].join('');

        const res = await withConfirmation({
          title: '建单提示',
          message,
          confirmButtonText: '打开工单',
          cancelButtonText: '关闭',
        });

        if (res) {
          skipDirtyCheck.current = true;
          finish({
            forceDiscardDraft: true,
            callback: () => {
              onNavigateToExistingTask(taskNo);
            },
          });
          return;
        }
      }

      performAutoFill();
    } catch (e) {
      console.error(e);
      fieldState.setLoading(false);
    }
  });
}
